import { useState, useEffect, useRef } from 'react';
import Sketch from './art/sketch';
import CandlestickChart from './art/candlestick-chart';
import { mountainRange } from '../hooks/art-styles/mountain-range';
import { citySkyline } from '../hooks/art-styles/city-skyline';
import { constellationMap } from '../hooks/art-styles/constellation-map';
import { kowloonCity } from '../hooks/art-styles/kowloon-city';

const ART_STYLE_MAP = {
    'Mountain Range': mountainRange,
    'Constellation Map': constellationMap,
    'City Skyline': citySkyline,
    Kowloon: kowloonCity,
};

const REVEAL_DELAY = 2500;

const ArtSection = ({ submission = null, chartData = null }) => {
    const [phase, setPhase] = useState('loading');
    const [regenKey, setRegenKey] = useState(0);
    const [showInfo, setShowInfo] = useState(false);
    const [prevGenUrl, setPrevGenUrl] = useState(null);
    const [showingPrev, setShowingPrev] = useState(false);
    const canvasContainerRef = useRef(null);

    useEffect(() => {
        if (!submission) return;
        setPhase('loading');
        setRegenKey(0);
        setShowInfo(false);
        setPrevGenUrl(null);
        setShowingPrev(false);
    }, [submission]);

    useEffect(() => {
        if (phase !== 'loading') return;
        const id = setTimeout(() => setPhase('ready'), REVEAL_DELAY);
        return () => clearTimeout(id);
    }, [phase]);

    const captureCurrentCanvas = () => {
        const container = canvasContainerRef.current;
        if (!container) return null;
        const canvas =
            container.querySelector('canvas[data-pixel-art-output="true"]') ??
            container.querySelector('canvas');
        return canvas ? canvas.toDataURL('image/png') : null;
    };

    const handleRegen = () => {
        const snapshot = captureCurrentCanvas();
        if (snapshot) setPrevGenUrl(snapshot);
        setShowInfo(false);
        setShowingPrev(false);
        setPhase('revealed');
        setRegenKey((k) => k + 1);
    };

    const handleLastGen = () => setShowingPrev((v) => !v);

    const handleSave = () => {
        const symbol = submission?.asset?.symbol ?? 'art';
        const style =
            submission?.artStyle?.name?.toLowerCase().replace(/\s+/g, '-') ??
            'chartit';
        const a = document.createElement('a');

        if (showingPrev && prevGenUrl) {
            a.href = prevGenUrl;
        } else {
            const container = canvasContainerRef.current;
            if (!container) return;
            const canvas =
                container.querySelector(
                    'canvas[data-pixel-art-output="true"]'
                ) ?? container.querySelector('canvas');
            if (!canvas) return;
            a.href = canvas.toDataURL('image/png');
        }

        a.download = `chartit-${symbol}-${style}.png`;
        a.click();
    };

    const handleBackToForm = () => {
        document
            .getElementById('form-section')
            ?.scrollIntoView({ behavior: 'smooth' });
    };

    const artStyle = submission?.artStyle?.name
        ? (ART_STYLE_MAP[submission.artStyle.name] ?? null)
        : null;

    if (!submission) return null;

    const submissionData = (
        <dl className="art-submission-debug">
            <div className="art-submission-row">
                <dt>Asset</dt>
                <dd>
                    {submission.asset.name} ({submission.asset.symbol}){' · '}
                    {submission.asset.type.toUpperCase()}
                    {submission.asset.exchange
                        ? ` · ${submission.asset.exchange}`
                        : ''}
                </dd>
            </div>
            <div className="art-submission-row">
                <dt>Timeframe</dt>
                <dd>{submission.timeframe}</dd>
            </div>
            <div className="art-submission-row">
                <dt>Date Range</dt>
                <dd>
                    {submission.dateRange.start} &rarr;{' '}
                    {submission.dateRange.end}
                </dd>
            </div>
            {chartData && (
                <>
                    <div className="art-submission-row">
                        <dt>Source</dt>
                        <dd>{chartData.source}</dd>
                    </div>
                    <div className="art-submission-row">
                        <dt>Candles</dt>
                        <dd>
                            {chartData.values.length} &middot;{' '}
                            {chartData.values[0]?.datetime} &rarr;{' '}
                            {
                                chartData.values[chartData.values.length - 1]
                                    ?.datetime
                            }
                        </dd>
                    </div>
                </>
            )}
            <div className="art-submission-row">
                <dt>Art Style</dt>
                <dd>{submission.artStyle.name}</dd>
            </div>
        </dl>
    );

    const warningsBlock =
        chartData?.warnings?.length > 0 ? (
            <ul className="art-warnings">
                {chartData.warnings.map((w) => (
                    <li key={w}>{w}</li>
                ))}
            </ul>
        ) : null;

    return (
        <section id="art-section">
            <div className="art-frame">
                <div className="art-frame-bar">
                    <span className="art-frame-title">
                        {submission.asset.symbol}
                        {submission.asset.exchange
                            ? ` · ${submission.asset.exchange}`
                            : ''}
                        {' · '}
                        {submission.timeframe}
                        {' · '}
                        {chartData?.values?.length ?? '…'} Candles &middot;{' '}
                        {chartData?.values?.[0]?.datetime ?? '…'} &rarr;{' '}
                        {chartData?.values?.at(-1)?.datetime ?? '…'}
                        {' · '}
                        {submission.artStyle.name}
                    </span>
                    <div className="art-frame-actions">
                        <button
                            type="button"
                            className="button art-frame-btn"
                            onClick={handleBackToForm}
                        >
                            Back to Form
                        </button>
                        <button
                            type="button"
                            className="button art-frame-btn"
                            disabled={phase !== 'revealed'}
                            onClick={handleSave}
                        >
                            Save Image
                        </button>
                        <button
                            type="button"
                            className={`button art-frame-btn${showInfo ? ' art-frame-btn--active' : ''}`}
                            disabled={phase !== 'revealed'}
                            onClick={() => setShowInfo((v) => !v)}
                            aria-pressed={showInfo}
                        >
                            Chart Overlay
                        </button>
                        <button
                            type="button"
                            className="button art-frame-btn"
                            disabled={!prevGenUrl || phase === 'loading'}
                            onClick={handleLastGen}
                        >
                            {showingPrev ? 'Current' : 'Previous Generation'}
                        </button>
                        <button
                            type="button"
                            className="button art-frame-btn"
                            disabled={phase === 'loading'}
                            onClick={handleRegen}
                        >
                            Regenerate
                        </button>
                    </div>
                </div>

                <div className="art-frame-canvas" ref={canvasContainerRef}>
                    {chartData && artStyle && (
                        <div
                            key={regenKey}
                            className="sketch-fade-in"
                            style={
                                showingPrev
                                    ? { visibility: 'hidden' }
                                    : undefined
                            }
                        >
                            <Sketch artStyle={artStyle} chartData={chartData} />
                        </div>
                    )}
                    {showingPrev && prevGenUrl && (
                        <img
                            src={prevGenUrl}
                            alt="Previous generation"
                            className="prev-gen-image"
                        />
                    )}

                    {phase !== 'revealed' && (
                        <div className="art-frame-overlay">
                            {submissionData}
                            {warningsBlock}
                            {phase === 'loading' && (
                                <div className="art-loading">
                                    <div className="art-loading-bar">
                                        <div className="art-loading-fill" />
                                    </div>
                                    <p className="art-loading-label">
                                        Art Generating...
                                    </p>
                                </div>
                            )}
                            {phase === 'ready' && (
                                <button
                                    className="button art-reveal-btn"
                                    onClick={() => setPhase('revealed')}
                                >
                                    Reveal
                                </button>
                            )}
                        </div>
                    )}

                    <CandlestickChart
                        chartData={chartData}
                        artStyleName={submission.artStyle.name}
                        visible={showInfo && phase === 'revealed'}
                    />
                </div>
            </div>
        </section>
    );
};

export default ArtSection;
